import React from 'react';

import { FormattedMessage } from '@99designs/common/i18n';
import {
    getSquarespaceBrief,
    getSquarespaceBriefVariables,
} from '__generated__/getSquarespaceBrief';
import { SquarespaceBriefFields } from '__generated__/SquarespaceBriefFields';
import { AuthConsumer } from 'projects/components/AuthContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { ApolloConsumer } from 'react-apollo';
import { RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import { getIndustry } from './industry';
import queries from './SquarespaceBrief.graphql';
import SummaryPage from './SummaryPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const steps: StepConfiguration[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
    },
    { step: 2, title: __('About you') },
    { step: 3, title: __('Review your brief') },
    {
        step: 4,
        title: __('Invite designers to quote'),
        requiresAuth: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

const SquarespaceBrief: React.FunctionComponent<Props> = props => (
    <BriefQuery<getSquarespaceBrief, getSquarespaceBriefVariables>
        query={queries.getSquarespaceBrief}
        variables={{ id: props.match.params.briefId }}
        fieldsTypename="SquarespaceBriefFields"
    >
        {(data, refetch) => {
            const brief = {
                ...data.brief,
                fields: data.brief.fields as SquarespaceBriefFields,
            };

            const description = () => {
                let selection = [brief.category && brief.category.title];

                const industry = getIndustry((brief.fields as SquarespaceBriefFields).industry);
                if (industry) {
                    selection = selection.concat(industry);
                }

                return (
                    <FormattedMessage
                        message={__(
                            "Based on your selection {selection} we've matched you with some awesome designers"
                        )}
                        values={{
                            selection: <strong>{selection.join(' / ')}</strong>,
                        }}
                    />
                );
            };

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    refetch={refetch}
                                    schema="squarespace"
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    step={createStepState({
                                        steps,
                                        brief: data.brief,
                                        currentStep: parseInt(props.match.params.step, 10),
                                        designerSources: data.brief.designersWithSource.map(
                                            d => d.source
                                        ),
                                    })}
                                    client={client}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/squarespace/:briefId/1`}
                                                render={() => (
                                                    <DetailsPage brief={brief} form={form} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/2`}
                                                render={() => (
                                                    <AboutYouPage form={form} brief={brief} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/3`}
                                                render={() => <SummaryPage brief={brief} />}
                                            />
                                            <Route
                                                path={`/briefs/squarespace/:briefId/4`}
                                                render={() => (
                                                    <InviteDesignersPage
                                                        brief={brief}
                                                        viewer={data.viewer}
                                                    >
                                                        {description()}
                                                    </InviteDesignersPage>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </BriefQuery>
);

export default withRouter(SquarespaceBrief);
